<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import FilePreviewDialog from './FilePreviewDialog.vue';
import { useFileActions } from '../../composables/useFileActions.js';
import { useAuth } from '../../composables/useAuth.js';
import { useConfirm } from '../../composables/useConfirm.js';

/* Menü zu einer Datei: Vorschau, Herunterladen, Löschen.
 *
 * Der Dateiname wird nirgends angezeigt (WhatsApp-Namen wie
 * IMG-20260420-WA0005.jpg sagen nichts). Gezeigt wird die Dokumentbezeichnung
 * (`name`), die der Aufrufer kennt. Der Name der Datei dient nur noch dem
 * Herunterladen und der Erkennung der Dateiart. */
const props = defineProps({
  file: { type: Object, default: null },
  /* Beschriftung des Knopfes; Vorgabe "Aktionen" */
  label: { type: String, default: '' },
  /* Dokumentbezeichnung für Vorschau-Titel und Rückfrage vor dem Löschen */
  name: { type: String, default: '' },
  /* Bisher für die Rechteprüfung beim Entfernen; das Löschen hängt jetzt an
     canDeleteFiles (nur admin, wie die Speicherregel). Bleibt, damit die
     Aufrufer unverändert weiter funktionieren. */
  area: { type: String, default: 'documents' },
  removable: { type: Boolean, default: true },
});
const emit = defineEmits(['remove']);

const { download } = useFileActions();
const { canDeleteFiles } = useAuth();
const { confirm } = useConfirm();
const { t } = useI18n();

const previewOpen = ref(false);

/* Zentrale Rückfrage vor dem Löschen einer Datei. Alle Gruppen nutzen dieses
   Menü, deshalb genügt die Prüfung hier — die Elternkomponente bekommt das
   Ereignis erst nach der Bestätigung. */
async function requestRemove() {
  menuOpen.value = false;
  const name = props.name || t('files.file');
  const ok = await confirm({
    title: t('confirm.deleteFileTitle'),
    message: t('confirm.deleteFile', { name }),
  });
  if (ok) emit('remove');
}

const menuOpen = ref(false);
const root = ref(null);

function onDocumentClick(e) {
  if (menuOpen.value && root.value && !root.value.contains(e.target)) menuOpen.value = false;
}
onMounted(() => document.addEventListener('click', onDocumentClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick));

function act(fn) {
  menuOpen.value = false;
  fn();
}
</script>

<template>
  <span ref="root" class="file-menu-wrap">
    <button
      type="button"
      class="file-menu-trigger"
      :aria-label="label || $t('files.actions')"
      aria-haspopup="menu"
      :aria-expanded="menuOpen"
      @click.stop="menuOpen = !menuOpen"
    >⋯</button>

    <div v-if="menuOpen" class="file-menu">
      <button @click.stop="act(() => (previewOpen = true))">{{ $t('files.preview') }}</button>
      <button @click.stop="act(() => download(file))">{{ $t('files.download') }}</button>
      <button
        v-if="removable && canDeleteFiles()"
        class="danger"
        @click.stop="requestRemove"
      >
        {{ $t('common.delete') }}
      </button>
    </div>

    <FilePreviewDialog
      v-if="previewOpen"
      :file="file"
      :title="name || label"
      @close="previewOpen = false"
    />
  </span>
</template>

<style scoped>
.file-menu-wrap {
  position: relative;
  display: inline-block;
}
/* Optisch an .icon-btn angelehnt (main.css), aber mit größerer Klickfläche
   fürs Handy — reiner Icon-Knopf hat sonst kaum Rand zum Antippen. */
.file-menu-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  min-height: 36px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--muted);
  font-size: 1rem;
  line-height: 1;
  font-family: var(--sans);
  cursor: pointer;
  padding: 0;
}
.file-menu-trigger:hover {
  color: var(--text);
  border-color: var(--dim);
}
.file-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  min-width: 150px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 8px 24px var(--shadow-strong);
  overflow: hidden;
}
.file-menu button {
  display: block;
  width: 100%;
  padding: 9px 14px;
  background: none;
  border: 0;
  text-align: left;
  color: var(--text);
  font-family: var(--sans);
  font-size: 0.78rem;
  cursor: pointer;
}
.file-menu button:hover {
  background: var(--card-hover);
}
.file-menu button.danger {
  color: var(--red);
  border-top: 1px solid var(--border-soft);
}
</style>
